// src/integrations/redux/app-api.ts
import { createApi } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { AuthState } from "@src/features/auth/auth-models";
import { baseQuery as rawBaseQuery } from "./base-query";
import { clearAuthState, setAuthState } from "@src/features/auth/redux/auth-slice";

const DEFAULT_KEEP_UNUSED_DATA_FOR_SEC = 60 * 15;

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  // 1) run original request
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error) {
    const err = result.error as FetchBaseQueryError;
    const url = typeof args === 'string' ? args : args.url;
    // These screens handle expired sessions and permissions inline, including
    // a return link. Retrying their mutations can duplicate a user action.
    if (url.includes('/auth/invite/') || /\/api\/v1\/rides\//.test(url) || url.startsWith('/api/v1/notifications')) return result;

    // 2) on 403 → try refresh
    if (err.status === 403 || (err.status==='PARSING_ERROR' && err.originalStatus===403)) {
      const refresh = await rawBaseQuery(
        { url: "/api/v1/auth/refreshToken", method: "POST" },
        api,
        extraOptions
      );
      if (refresh.data) {
		    api.dispatch(setAuthState(refresh.data as AuthState))
        // retry original
        result = await rawBaseQuery(args, api, extraOptions);
        if (result.error){
          const err2 = result.error as FetchBaseQueryError;
          if (err2.status === 403 || (err2.status==='PARSING_ERROR' && err2.originalStatus===403)) {
            alert('You do not have the permission for this action, contact system manager')
          }
        }
      } 
    }
    // 3) on 401 → clear auth and bubble up
    if (err.status === 401 || (err.status==='PARSING_ERROR' && err.originalStatus===401)) {
      api.dispatch(clearAuthState());
      return {
        error: { status: 401, data: "Unauthorized" } as FetchBaseQueryError,
      };
    }
  }

  return result;
};

export const appApi = createApi({
  reducerPath: "appApi",
  baseQuery: baseQueryWithReauth,
  keepUnusedDataFor: DEFAULT_KEEP_UNUSED_DATA_FOR_SEC,
  endpoints: (builder) => ({}),
});
